<template>
  <div class="admin-layout">
    <!-- 密码验证遮罩层 -->
    <div v-if="!isAuthenticated" class="password-overlay">
      <div class="password-dialog">
        <div class="password-header">
          <el-icon><Lock /></el-icon>
          <h2>管理后台登录</h2>
          <p>请输入访问密码</p>
        </div>
        <div class="password-body">
          <el-input
            v-model="password"
            type="password"
            placeholder="请输入密码"
            size="large"
            @keyup.enter="verifyPassword"
            :class="{ 'error': passwordError }"
          />
          <p v-if="passwordError" class="error-message">密码错误，请重试</p>
          <el-button
            type="primary"
            size="large"
            @click="verifyPassword"
            :loading="verifying"
            style="width: 100%; margin-top: 20px;"
          >
            进入管理后台
          </el-button>
        </div>
      </div>
    </div>

    <template v-else>
      <!-- 侧边栏 -->
      <aside class="sidebar" :class="{ collapsed: sidebarCollapsed }">
        <div class="sidebar-header">
          <div class="logo">
            <el-icon><Setting /></el-icon>
            <span v-if="!sidebarCollapsed" class="logo-text">{{ $t('admin.layout.title') }}</span>
          </div>
          <button @click="toggleSidebar" class="sidebar-toggle">
            <el-icon><Expand /></el-icon>
          </button>
        </div>

        <nav class="sidebar-nav">
          <ul class="nav-list">
            <li class="nav-item">
              <router-link to="/admin/registrations" class="nav-link" active-class="active">
                <el-icon><User /></el-icon>
                <span v-if="!sidebarCollapsed" class="nav-text">{{ $t('admin.layout.menu.registrations') }}</span>
              </router-link>
            </li>
            <li class="nav-item">
              <router-link to="/admin/submissions" class="nav-link" active-class="active">
                <el-icon><Document /></el-icon>
                <span v-if="!sidebarCollapsed" class="nav-text">{{ $t('admin.layout.menu.submissions') }}</span>
              </router-link>
            </li>
            <li class="nav-item">
              <router-link to="/admin/speakers" class="nav-link" active-class="active">
                <el-icon><Microphone /></el-icon>
                <span v-if="!sidebarCollapsed" class="nav-text">{{ $t('admin.layout.menu.speakers') }}</span>
              </router-link>
            </li>
          </ul>
        </nav>

        <!-- 退出登录 -->
        <div class="sidebar-footer" v-if="!sidebarCollapsed">
          <el-button type="danger" text @click="logout" style="width: 100%;">
            <el-icon><SwitchButton /></el-icon>
            退出登录
          </el-button>
        </div>
        <div class="sidebar-footer collapsed" v-else>
          <el-button type="danger" text @click="logout" circle>
            <el-icon><SwitchButton /></el-icon>
          </el-button>
        </div>
      </aside>

      <!-- 主内容区域 -->
      <div class="main-content" :class="{ expanded: sidebarCollapsed }">
        <!-- 顶部导航栏 -->
        <header class="top-header">
          <div class="header-left">
            <h1 class="page-title">{{ getPageTitle() }}</h1>
          </div>
          <div class="header-right">
            <div class="user-menu">
              <div class="user-avatar">
                <el-icon><Avatar /></el-icon>
              </div>
              <span class="user-name">Admin</span>
            </div>
          </div>
        </header>

        <!-- 面包屑导航 -->
        <div class="breadcrumb">
          <router-link to="/" class="breadcrumb-item">
            <el-icon><HomeFilled /></el-icon>
            {{ $t('common.goHome') }}
          </router-link>
          <span class="breadcrumb-separator">/</span>
          <span class="breadcrumb-item current">{{ getPageTitle() }}</span>
        </div>

        <!-- 页面内容 -->
        <main class="page-content">
          <router-view />
        </main>
      </div>
    </template>
  </div>
</template>

<script>
import { Setting, Expand, User, Document, Microphone, Avatar, HomeFilled, Lock, SwitchButton } from '@element-plus/icons-vue'

export default {
  name: 'AdminLayout',
  components: {
    Setting,
    Expand,
    User,
    Document,
    Microphone,
    Avatar,
    HomeFilled,
    Lock,
    SwitchButton
  },
  data() {
    return {
      sidebarCollapsed: false,
      isAuthenticated: false,
      password: '',
      passwordError: false,
      verifying: false
    }
  },
  methods: {
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed
    },

    switchLanguage(lang) {
      this.$i18n.locale = lang
      localStorage.setItem('language', lang)
    },

    getPageTitle() {
      const routeName = this.$route.name
      switch (routeName) {
        case 'AdminRegistrations':
          return this.$t('admin.registrations.title')
        case 'AdminSubmissions':
          return this.$t('admin.submissions.title')
        case 'AdminSpeakers':
          return this.$t('admin.speakers.title')
        default:
          return this.$t('admin.layout.title')
      }
    },

    verifyPassword() {
      this.verifying = true
      this.passwordError = false

      // 模拟验证延迟，防止暴力破解
      setTimeout(() => {
        const correctPassword = process.env.VUE_APP_ADMIN_PASSWORD

        if (this.password === correctPassword) {
          this.isAuthenticated = true
          sessionStorage.setItem('admin_authenticated', 'true')
        } else {
          this.passwordError = true
        }
        this.verifying = false
      }, 300)
    },

    logout() {
      this.isAuthenticated = false
      this.password = ''
      sessionStorage.removeItem('admin_authenticated')
    },

    checkAuthentication() {
      // 检查 sessionStorage 中是否有认证标记
      const authStatus = sessionStorage.getItem('admin_authenticated')
      if (authStatus === 'true') {
        this.isAuthenticated = true
      }
    }
  },

  mounted() {
    this.checkAuthentication()

    this.$i18n.locale = 'zh'
    document.title = this.$t('admin.layout.title')
  }
}
</script>

<style scoped>
.admin-layout {
  display: flex;
  height: 100vh;
  background-color: #f5f5f5;
}

/* 页面整体 */
.admin-layout {
  display: flex;
  height: 100vh;
  background: #f6f8fb;
}

/* 登录遮罩层 */
.password-overlay {
  position: fixed;
  inset: 0;
  background:
    radial-gradient(circle at top left, rgba(64, 158, 255, 0.08), transparent 35%),
    radial-gradient(circle at bottom right, rgba(103, 194, 58, 0.06), transparent 35%),
    #f6f8fb;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 9999;
}

/* 登录卡片 */
.password-dialog {
  width: 100%;
  max-width: 380px;
  padding: 40px 36px;
  border-radius: 24px;

  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(16px);

  border: 1px solid rgba(255, 255, 255, 0.7);

  box-shadow:
    0 10px 30px rgba(15, 23, 42, 0.06),
    0 2px 8px rgba(15, 23, 42, 0.04);

  animation: loginFade 0.35s ease;
}

@keyframes loginFade {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 头部 */
.password-header {
  text-align: center;
  margin-bottom: 32px;
}

.password-header .el-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 18px;

  display: flex;
  align-items: center;
  justify-content: center;

  font-size: 28px;

  border-radius: 20px;

  background: linear-gradient(135deg, #409eff 0%, #79bbff 100%);
  color: white;

  box-shadow: 0 8px 20px rgba(64, 158, 255, 0.25);
}

.password-header h2 {
  margin: 0 0 10px;
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: 0.5px;
}

.password-header p {
  margin: 0;
  font-size: 14px;
  color: #94a3b8;
}

/* 输入区域 */
.password-body {
  width: 100%;
}

/* 输入框优化 */
.password-body :deep(.el-input__wrapper) {
  height: 50px;
  border-radius: 14px;
  background: #f8fafc;
  box-shadow: none;
  border: 1px solid transparent;
  transition: all 0.25s ease;
}

.password-body :deep(.el-input__wrapper:hover) {
  background: #fff;
  border-color: #dbeafe;
}

.password-body :deep(.el-input__wrapper.is-focus) {
  background: #fff;
  border-color: #409eff;
  box-shadow: 0 0 0 4px rgba(64, 158, 255, 0.12);
}

.password-body :deep(.el-input__inner) {
  font-size: 15px;
  color: #334155;
}

/* 按钮 */
.password-body .el-button {
  width: 100%;
  height: 50px;
  margin-top: 22px;

  border: none;
  border-radius: 14px;

  font-size: 15px;
  font-weight: 600;

  background: linear-gradient(135deg, #409eff 0%, #3375ff 100%);

  box-shadow: 0 8px 18px rgba(64, 158, 255, 0.22);

  transition: all 0.25s ease;
}

.password-body .el-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 24px rgba(64, 158, 255, 0.28);
}

/* 错误提示 */
.error-message {
  margin-top: 10px;
  text-align: center;
  font-size: 13px;
  color: #ef4444;
}

/* 移动端 */
@media (max-width: 768px) {
  .password-dialog {
    padding: 32px 24px;
    border-radius: 20px;
  }

  .password-header h2 {
    font-size: 24px;
  }
}

/* 侧边栏样式 */
.sidebar {
  width: 250px;
  background: linear-gradient(180deg, #2c3e50 0%, #34495e 100%);
  color: white;
  transition: width 0.3s ease;
  position: relative;
  z-index: 1000;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.sidebar.collapsed {
  width: 70px;
}

.sidebar-header {
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 18px;
  font-weight: 600;
}

.logo i {
  font-size: 24px;
  color: #3498db;
}

.logo-text {
  white-space: nowrap;
  overflow: hidden;
}

.sidebar-toggle {
  background: none;
  border: none;
  color: white;
  font-size: 16px;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

.sidebar-toggle:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.sidebar-nav {
  padding: 20px 0;
  flex: 1;
}

.nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.nav-item {
  margin-bottom: 8px;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s ease;
  border-left: 3px solid transparent;
}

.nav-link:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  border-left-color: #3498db;
}

.nav-link.active {
  background-color: rgba(52, 152, 219, 0.2);
  color: white;
  border-left-color: #3498db;
}

.nav-link i {
  font-size: 16px;
  width: 20px;
  text-align: center;
}

.nav-text {
  white-space: nowrap;
  overflow: hidden;
}

/* 侧边栏底部退出按钮 */
.sidebar-footer {
  padding: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  i {
    margin-right: 8px;
  }
}

.sidebar-footer.collapsed {
  display: flex;
  justify-content: center;

  i {
    margin-right: 0px;
  }
}

/* 主内容区域 */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  transition: margin-left 0.3s ease;
  width: calc(100% - 250px);
}

.main-content.expanded {
  margin-left: 0;
}

/* 顶部导航栏 */
.top-header {
  background: white;
  padding: 0 30px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid #e8e8e8;
}

.header-left {
  display: flex;
  align-items: center;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.language-switcher {
  display: flex;
  background: linear-gradient(145deg, #ffffff, #f0f0f0);
  border-radius: 12px;
  padding: 3px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
}

.lang-btn {
  padding: 8px 16px;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 2;
  min-width: 45px;
  text-transform: uppercase;
}

.lang-btn.active {
  background: linear-gradient(135deg, #3498db, #2980b9);
  color: white;
  box-shadow:
    0 4px 12px rgba(52, 152, 219, 0.3),
    0 2px 4px rgba(52, 152, 219, 0.2);
  transform: translateY(-1px);
}

.lang-btn:hover:not(.active) {
  background: linear-gradient(145deg, #f8fafc, #e2e8f0);
  color: #475569;
  transform: translateY(-0.5px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.lang-btn:active {
  transform: translateY(0);
  transition: transform 0.1s ease;
}

.user-menu {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  background: linear-gradient(145deg, #ffffff, #f0f0f0);
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.user-avatar i {
  font-size: 24px;
  color: #3498db;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: #2c3e50;
}

/* 面包屑导航 */
.breadcrumb {
  padding: 15px 30px;
  background: white;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.breadcrumb-item {
  color: #666;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: color 0.3s ease;
}

.breadcrumb-item:hover {
  color: #3498db;
}

.breadcrumb-item.current {
  color: #2c3e50;
  font-weight: 500;
}

.breadcrumb-separator {
  color: #ccc;
}

/* 页面内容 */
.page-content {
  flex: 1;
  padding: 30px;
  overflow-y: auto;
  background-color: #f5f5f5;
}

</style>
